/**
 * SubscriptionActionsPanel — iter-230bw Phase 5 (admin)
 * ------------------------------------------------------
 * Sits on the admin Vendor Billing detail view. Wraps the new
 * /api/admin/subscriptions/* endpoints into a single quick-actions
 * panel: Cancel, Change Plan, Manual Refund, and an audit timeline
 * drawer.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  XCircle, ArrowUpDown, IndianRupee, ClipboardList, Loader2, RefreshCw,
} from 'lucide-react';

const PLAN_CODES = ['launch', 'basic', 'professional', 'business', 'enterprise'];

const formatINR = (v) =>
  v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN'); } catch { return iso; }
};

export default function SubscriptionActionsPanel({ vendorId, vendorEmail, onChanged }) {
  const { apiCall } = useAuth();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Form state
  const [cancelReason, setCancelReason] = useState('');
  const [newPlanCode, setNewPlanCode] = useState('basic');
  const [changeReason, setChangeReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Audit
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadAudit = async () => {
    if (!vendorId) return;
    setAuditLoading(true);
    try {
      const res = await apiCall(`/api/admin/subscriptions/${vendorId}/audit?limit=100`);
      setAudit(res?.audit_logs || []);
    } catch (e) {
      toast.error(e?.message || 'Could not load audit log.');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (auditOpen) loadAudit();
    // eslint-disable-next-line
  }, [auditOpen, vendorId]);

  const cancelSubscription = async () => {
    if (!cancelReason) { toast.error('Reason is required.'); return; }
    setBusy(true);
    try {
      await apiCall(`/api/admin/subscriptions/${vendorId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: cancelReason, refund_annual: false }),
      });
      toast.success(`Subscription cancelled for ${vendorEmail || vendorId}.`);
      setCancelOpen(false);
      setCancelReason('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Cancel failed.');
    } finally { setBusy(false); }
  };

  const changePlan = async () => {
    if (!newPlanCode || !changeReason) { toast.error('Plan and reason required.'); return; }
    setBusy(true);
    try {
      const res = await apiCall(`/api/admin/subscriptions/${vendorId}/change-plan`, {
        method: 'POST',
        body: JSON.stringify({ new_plan_code: newPlanCode, reason: changeReason }),
      });
      toast.success(`Plan changed: ${res.from_plan_code} → ${res.to_plan_code}`);
      setChangeOpen(false);
      setChangeReason('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Change plan failed.');
    } finally { setBusy(false); }
  };

  // iter-296 — One-click Razorpay sync. Heals rows stuck at
  // status='pending' when the vendor's checkout modal timed out but the
  // Razorpay mandate went live (e.g. Harsha / Basic monthly).
  const [syncBusy, setSyncBusy] = useState(false);
  const syncFromRazorpay = async () => {
    setSyncBusy(true);
    try {
      const res = await apiCall(`/api/vendor-subscription/admin/reconcile-razorpay/${vendorId}`, {
        method: 'POST',
        body: JSON.stringify({ apply: true }),
      });
      if (res?.healed) {
        toast.success(`Synced from Razorpay — healed: ${Object.keys(res.healed).filter(k => !['updated_at', 'reconciled_at'].includes(k)).join(', ') || 'status'}`);
      } else {
        toast.info(res?.note || 'No drift — already in sync with Razorpay.');
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Razorpay sync failed.');
    } finally { setSyncBusy(false); }
  };

  const manualRefund = async () => {
    const amt = Number(refundAmount);
    if (!amt || amt <= 0 || !refundReason) {
      toast.error('Amount and reason required.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiCall(`/api/admin/subscriptions/${vendorId}/refund`, {
        method: 'POST',
        body: JSON.stringify({ amount_rupees: amt, reason: refundReason }),
      });
      toast.success(
        `Refund processed · credit note ${res.credit_note_number}`
        + (res.razorpay_refund_id ? ` · ${res.razorpay_refund_id}` : '')
      );
      setRefundOpen(false);
      setRefundAmount('');
      setRefundReason('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Refund failed.');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="subscription-actions-panel">
      <CardHeader>
        <CardTitle className="text-base">Subscription Actions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={syncFromRazorpay} disabled={syncBusy} data-testid="admin-sync-razorpay-btn">
            {syncBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Sync from Razorpay
          </Button>
          <Button variant="outline" size="sm" onClick={() => setChangeOpen(true)} data-testid="admin-change-plan-btn">
            <ArrowUpDown className="h-4 w-4 mr-1" /> Change plan
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRefundOpen(true)} data-testid="admin-refund-btn">
            <IndianRupee className="h-4 w-4 mr-1" /> Manual refund
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} data-testid="admin-audit-btn">
            <ClipboardList className="h-4 w-4 mr-1" /> Audit log
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setCancelOpen(true)} data-testid="admin-cancel-btn">
            <XCircle className="h-4 w-4 mr-1" /> Cancel subscription
          </Button>
        </div>
      </CardContent>

      {/* Cancel modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel subscription</DialogTitle>
            <DialogDescription>
              This cancels the vendor&apos;s subscription at Razorpay (where applicable) and marks it as
              cancelled in the DB. Use Manual Refund afterwards if a refund is owed.
            </DialogDescription>
          </DialogHeader>
          <Label>Reason *</Label>
          <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. vendor request, support ticket #1234" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={busy}>Close</Button>
            <Button variant="destructive" onClick={cancelSubscription} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirm cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change plan modal */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change plan (override)</DialogTitle>
            <DialogDescription>
              Forces the vendor onto the selected plan without charging. Use this only when the
              vendor has been billed off-platform or is being comped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New plan</Label>
              <Select value={newPlanCode} onValueChange={setNewPlanCode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_CODES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea value={changeReason} onChange={(e) => setChangeReason(e.target.value)} placeholder="e.g. comped enterprise, off-platform billing" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setChangeOpen(false)} disabled={busy}>Close</Button>
            <Button onClick={changePlan} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund modal */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual refund + credit note</DialogTitle>
            <DialogDescription>
              Initiates a Razorpay refund (if a payment id exists) and issues a GST credit note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (₹) *</Label>
              <Input type="number" min="0" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder="e.g. 1500" />
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="e.g. goodwill refund per support ticket #1234" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={busy}>Close</Button>
            <Button onClick={manualRefund} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Process refund
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit modal */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Subscription audit log</DialogTitle>
            <DialogDescription>
              Newest first. Covers webhook events, vendor + admin actions, dunning, reconciliation drift.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2" data-testid="admin-audit-list">
            {auditLoading && <p className="text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</p>}
            {!auditLoading && audit.length === 0 && (
              <p className="text-sm text-slate-500 italic">No audit entries yet.</p>
            )}
            {audit.map((row) => (
              <div key={row.id} className="border-l-2 pl-3 py-1 text-xs"
                   style={{ borderColor: row.severity === 'error' ? '#dc2626' : row.severity === 'warning' ? '#f59e0b' : '#94a3b8' }}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize text-[10px]">{row.event_type?.replace(/_/g, ' ')}</Badge>
                  <span className="text-slate-500">{formatDate(row.created_at)}</span>
                  <span className="text-slate-400">· {row.actor || '—'}</span>
                </div>
                {row.payload && Object.keys(row.payload).length > 0 && (
                  <pre className="mt-1 bg-slate-50 p-1 rounded text-[10px] overflow-x-auto">
                    {JSON.stringify(row.payload, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
